import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import DataTable from '../../components/common/DataTable';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { adminsApi } from '../../api/admins.api';
import { useAuth } from '../../context/AuthContext';
import { PlusIcon, TrashIcon, PencilIcon, NoSymbolIcon, CheckIcon } from '@heroicons/react/24/outline';

const columns = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  {
    key: 'role',
    label: 'Role',
    render: (row) => (
      <Badge variant={row.role?.name === 'superadmin' ? 'danger' : row.role?.name === 'admin' ? 'primary' : 'secondary'}>
        {row.role?.name || 'N/A'}
      </Badge>
    )
  },
  {
    key: 'isActive',
    label: 'Status',
    render: (row) =>
      row.isActive ? (
        <Badge variant="success">Active</Badge>
      ) : (
        <Badge variant="danger">Inactive</Badge>
      )
  },
  {
    key: 'lastLogin',
    label: 'Last Login',
    render: (row) =>
      row.lastLogin ? new Date(row.lastLogin).toLocaleDateString() : 'Never'
  }
];

export default function AdminsListPage() {
  const navigate = useNavigate();
  const { admin: currentAdmin } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const fetchAdmins = async (page = 1, searchTerm = '') => {
    setLoading(true);
    try {
      const response = await adminsApi.getAll({ page, search: searchTerm, limit: 20 });
      setAdmins(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching admins:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins(1, search);
  }, [search]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await adminsApi.delete(deleteId);
      setDeleteId(null);
      fetchAdmins(pagination.page, search);
    } catch (error) {
      console.error('Error deleting admin:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (id) => {
    setTogglingId(id);
    try {
      const response = await adminsApi.toggleActive(id);
      setAdmins(prev => prev.map(admin =>
        admin._id === id ? response.data.admin : admin
      ));
    } catch (error) {
      console.error('Error toggling admin status:', error);
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Admins</h1>
        <Button onClick={() => navigate('/admins/new')}>
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Admin
        </Button>
      </div>

      <DataTable
        columns={[
          ...columns,
          {
            key: 'actions',
            label: 'Actions',
            align: 'right',
            render: (row) => (
              <div className="flex gap-2 justify-end">
                {row._id !== currentAdmin?.id && (
                  <button
                    onClick={() => handleToggleActive(row._id)}
                    disabled={togglingId === row._id}
                    className={`p-1 ${row.isActive ? 'text-gray-500 hover:text-red-600' : 'text-gray-500 hover:text-green-600'}`}
                    title={row.isActive ? 'Deactivate' : 'Activate'}
                  >
                    {togglingId === row._id ? (
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
                    ) : row.isActive ? (
                      <NoSymbolIcon className="h-5 w-5" />
                    ) : (
                      <CheckIcon className="h-5 w-5" />
                    )}
                  </button>
                )}
                <button
                  onClick={() => navigate(`/admins/${row._id}/edit`)}
                  className="p-1 text-gray-500 hover:text-primary-600"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setDeleteId(row._id)}
                  className="p-1 text-gray-500 hover:text-red-600"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            )
          }
        ]}
        data={admins}
        loading={loading}
        pagination={pagination}
        onPageChange={(page) => fetchAdmins(page, search)}
        onSearch={setSearch}
      />

      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Admin"
        message="Are you sure you want to delete this admin?"
        confirmText="Delete"
        loading={deleting}
      />
    </DashboardLayout>
  );
}
